import {MerchantConfiguration} from './../../reducers/merchantConfigurationReducer';
import {loadScript, type PayPalNamespace} from '@paypal/paypal-js';
import {FeatureFlag} from '../../../../@type/features';
import {type GatewayConfiguration} from '../../models/GatewayConfiguration';
import {Feature, startModalProcessing} from '../../reducers/environmentReducer';
import {PaymentConfiguration, registerGatewayBatch} from '../../reducers/paymentConfigurationReducer';
import {store} from '../../store';
import {getLocaleText} from '../../util/translation';
import {createExpressCheckoutPaymentAttempt, type OrderService} from '../order';
import {setupPayPalHostedButtonRendering, setupPayPalNonHostedButtonRendering} from './button';
import {initPayPalCreditMethod} from './paypal-credit';
import {initPayPalPayLaterMethod} from './paypal-paylater';
import {initPayPalVenmoMethod} from './paypal-venmo';
import {initPayPalWalletMethod} from './paypal-wallet';
import {initPayPalCardMethod} from './paypal-card';
import {captureSentryException} from '../../../../@shared/ts/sentry';
import {type PaymentAttempt, type PaymentData} from '../../../../@shared/ts/paymentAttempt';
import {$qsAll} from '../../../../@shared/ts/dom';
import {PeachPayOrder} from '../../reducers/peachPayOrderReducer';

export default async function initPayPalPaymentIntegration(orderService: OrderService): Promise<void> {
	if (!Feature.enabled(FeatureFlag.PAYPAL_GATEWAYS)) {
		return;
	}

	const clientId = Feature.metadata<string>(FeatureFlag.PAYPAL_GATEWAYS, 'client_id');
	const merchantId = Feature.metadata<string>(FeatureFlag.PAYPAL_GATEWAYS, 'merchant_id');
	const partnerAttributionId = Feature.metadata<string>(FeatureFlag.PAYPAL_GATEWAYS, 'partner_attribution_id');

	if (!clientId || !merchantId || !partnerAttributionId) {
		return;
	}

	const hostedButtonGateways = registerPayPalHostedButtonGateways();
	const nonHostedButtonGateways = registerPayPalNonHostedButtonGateways();

	setupPayPalHostedButtonRendering(hostedButtonGateways);
	setupPayPalNonHostedButtonRendering(nonHostedButtonGateways);

	const presentmentCurrencies = new Set(['USD', 'AUD', 'BRL', 'CAD', 'CZK', 'DKK', 'EUR', 'HKD', 'HUF', 'ILS', 'JPY', 'MYR', 'MXN', 'TWD', 'NZD', 'NOK', 'PHP', 'PLN', 'GBP', 'RUB', 'SGD', 'SEK', 'CHF', 'THB']);
	let previousCurrency = '';
	store.subscribe(async () => {
		const currentCurrency = MerchantConfiguration.currency.code();
		if (currentCurrency === previousCurrency) {
			return;
		}

		previousCurrency = currentCurrency;

		// Reset all PayPal payment methods to there default state when the currency changes.
		for (const [_, gateway] of Object.entries(hostedButtonGateways)) {
			gateway.reset();
			gateway.state = undefined;
		}

		// If the new currency is not a supported presentment currency then do not reinitialize payment methods.
		if (!presentmentCurrencies.has(currentCurrency)) {
			return;
		}

		try {
			const paypal = await loadScript({
				clientId,
				merchantId,
				dataPartnerAttributionId: partnerAttributionId,
				enableFunding: 'venmo',
				currency: MerchantConfiguration.currency.code(),
				dataPageType: 'checkout',
				components: ['buttons', 'card-fields'],
			});

			if (!paypal) {
				return;
			}

			await activatePayPalHostedButtonGateways(paypal, hostedButtonGateways, orderService);
			await activatePayPalNonHostedButtonGateways(paypal, nonHostedButtonGateways);
		} catch (error) {
			captureSentryException(new Error('PayPal SDK failed to load'), {
				error,
			});
		}
	});

	const confirm = async (event: MouseEvent) => {
		if (!await PeachPayOrder.reportValidity()) {
			return;
		}

		const $target = event.target as HTMLElement | null;
		const $button = $target?.closest<HTMLButtonElement>('button');
		if (!$button) {
			return;
		}

		const gatewayId = PaymentConfiguration.selectedGateway();
		const gateway = nonHostedButtonGateways[gatewayId];
		if (!gateway) {
			return;
		}

		store.dispatch(startModalProcessing());

		gateway.submit(createExpressCheckoutPaymentAttempt(orderService));
	};

	$qsAll<HTMLElement>('.paypal-btn', $el => {
		$el.addEventListener('click', async event => {
			await confirm(event);
		});
	});
}

function registerPayPalNonHostedButtonGateways(): Record<string, PayPalGateway> {
	const gatewayImplementations: Record<string, PayPalGateway> = {};
	const gatewayConfigurations: Record<string, GatewayConfiguration> = {};

	if (Feature.enabled(FeatureFlag.PAYPAL_CARD_GATEWAY)) {
		const method = initPayPalCardMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	store.dispatch(registerGatewayBatch(gatewayConfigurations));

	return gatewayImplementations;
}

function registerPayPalHostedButtonGateways(): Record<string, PayPalButtonGateway> {
	const gatewayImplementations: Record<string, PayPalButtonGateway> = {};
	const gatewayConfigurations: Record<string, GatewayConfiguration> = {};

	if (Feature.enabled(FeatureFlag.PAYPAL_WALLET_GATEWAY)) {
		const method = initPayPalWalletMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.PAYPAL_VENMO_GATEWAY)) {
		const method = initPayPalVenmoMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.PAYPAL_PAYLATER_GATEWAY)) {
		const method = initPayPalPayLaterMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.PAYPAL_CREDIT_GATEWAY)) {
		const method = initPayPalCreditMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	store.dispatch(registerGatewayBatch(gatewayConfigurations));

	return gatewayImplementations;
}

async function activatePayPalHostedButtonGateways(paypal: PayPalNamespace, gateways: Record<string, PayPalButtonGateway>, orderService: OrderService) {
	const activations = [];
	for (const [_, gateway] of Object.entries(gateways)) {
		if (gateway.activate) {
			activations.push(gateway.activate(paypal, orderService));
		}
	}

	await Promise.all(activations);
}

async function activatePayPalNonHostedButtonGateways(paypal: PayPalNamespace, gateways: Record<string, PayPalGateway>) {
	const activations = [];
	for (const [_, gateway] of Object.entries(gateways)) {
		if (gateway.activate) {
			activations.push(gateway.activate(paypal));
		}
	}

	await Promise.all(activations);
}

function paypalDescription(title: string, icon: string, description: string) {
	return /* html */ `
    <img style="display: block; text-align: left; height: 1.7rem; " src="${icon}">
    <p style="text-align: left; margin: 0.5rem 0;">${title} ${getLocaleText('selected for checkout.')}</p>
    <hr>
    <p style="text-align: left; margin: 0.5rem 0 0;" class="muted">${description}</p>`;
}

/**
 * Implementation type for a PayPal gateway
 */
interface PayPalButtonGateway {
	state?: {
		mountTarget: string;
	};
	config: GatewayConfiguration;
	activate(paypal: PayPalNamespace, orderService: OrderService): Promise<void> | void;
	reset(): void;
	attach(baseTarget: string): void;
	detach(): void;
}

interface PayPalGateway {
	config: GatewayConfiguration;
	activate(paypal: PayPalNamespace): Promise<void> | void;
	submit(paymentAttempt: PaymentAttempt): void;
}

interface PayPalPaymentData extends PaymentData {
	'data': {
		'id': string;
	};
}

export {
	paypalDescription,
	type PayPalButtonGateway,
	type PayPalPaymentData,
	type PayPalGateway,
};
